import { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useLang } from "@/context/AppContext";
import { PageHead, GhostBtn } from "@/pages/app/Dashboard";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { useApi } from "@/lib/useApi";

const WaitlistTab = () => {
  const { t } = useLang();
  const { data: rows, reload: load } = useApi("/admin/waitlist", { initial: [] });
  const setStatus = async (id, status) => { try { await api.patch(`/admin/waitlist/${id}`, { status }); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Table data-testid="admin-waitlist-table">
      <TableHeader><TableRow><TableHead>{t("name")}</TableHead><TableHead>Email</TableHead><TableHead>{t("kind")}</TableHead><TableHead>{t("sector")}</TableHead><TableHead>{t("status")}</TableHead><TableHead /></TableRow></TableHeader>
      <TableBody>
        {rows.map((w) => (
          <TableRow key={w.id} data-testid={`waitlist-row-${w.id}`}>
            <TableCell>{w.name}</TableCell><TableCell className="font-mono text-xs">{w.email}</TableCell><TableCell>{t(w.kind)}</TableCell><TableCell>{w.sector}</TableCell>
            <TableCell className="font-mono text-xs uppercase" data-testid={`waitlist-status-${w.id}`}>{w.status}</TableCell>
            <TableCell className="space-x-2 text-right">
              <button onClick={() => setStatus(w.id, "approved")} className="text-xs text-emerald-300 hover:underline" data-testid={`waitlist-approve-${w.id}`}>{t("approve")}</button>
              <button onClick={() => setStatus(w.id, "rejected")} className="text-xs text-rose-300 hover:underline" data-testid={`waitlist-reject-${w.id}`}>{t("reject")}</button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const AuditTab = () => {
  const { t } = useLang();
  const [filter, setFilter] = useState("");
  const [chain, setChain] = useState(null);
  const { data: rows } = useApi("/admin/audit", { initial: [], params: filter ? { event: filter } : {}, delay: 250 });
  const verify = async () => { const { data } = await api.get("/admin/audit/verify"); setChain(data); };
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Input className="max-w-xs" placeholder={t("filter")} value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="audit-filter-input" />
        <GhostBtn onClick={verify} data-testid="verify-chain-btn"><ShieldCheck className="h-4 w-4" />{t("verify_chain")}</GhostBtn>
        {chain && <span className={`font-mono text-xs ${chain.valid ? "text-emerald-300" : "text-rose-300"}`} data-testid="chain-status">{t(chain.valid ? "chain_ok" : "chain_bad")} · {chain.checked}</span>}
      </div>
      <Table data-testid="admin-audit-log-table">
        <TableHeader><TableRow><TableHead>{t("date")}</TableHead><TableHead>{t("event")}</TableHead><TableHead>{t("actor")}</TableHead><TableHead>Entity</TableHead><TableHead>Hash</TableHead></TableRow></TableHeader>
        <TableBody>
          {rows.map((a) => (
            <TableRow key={a.id}>
              <TableCell className="font-mono text-xs text-slate-400">{new Date(a.at).toLocaleString()}</TableCell>
              <TableCell className="font-mono text-xs text-emerald-300">{a.event}</TableCell>
              <TableCell className="text-xs">{a.actor_email || "public"}</TableCell>
              <TableCell className="font-mono text-xs text-slate-400">{a.entity_type} {a.entity_id && a.entity_id.slice(-6)}</TableCell>
              <TableCell className="font-mono text-[10px] text-slate-500">{a.hash.slice(0, 12)}…</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default function Admin() {
  const { t } = useLang();
  const { data: stats } = useApi("/admin/stats");
  return (
    <div>
      <PageHead title={t("admin_title")} sub={stats && `users ${stats.users} · waitlist ${stats.waitlist} · relationships ${stats.relationships} · audit ${stats.audit_events}`} />
      <Tabs defaultValue="waitlist">
        <TabsList><TabsTrigger value="waitlist" data-testid="admin-tab-waitlist">{t("waitlist")}</TabsTrigger><TabsTrigger value="audit" data-testid="admin-tab-audit">{t("audit")}</TabsTrigger></TabsList>
        <TabsContent value="waitlist" className="mt-6 rounded-md border border-white/[0.08] bg-[#111827] p-4"><WaitlistTab /></TabsContent>
        <TabsContent value="audit" className="mt-6 rounded-md border border-white/[0.08] bg-[#111827] p-4"><AuditTab /></TabsContent>
      </Tabs>
    </div>
  );
}
